import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  CalendarDays,
  MapPin,
  Sparkles,
  Award,
  Users,
  BookOpen,
  Leaf,
  HeartPulse,
  Stethoscope,
  Youtube,
  PlayCircle,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { videosApi, postsApi, settingsApi, committeeApi, galleryApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useSiteImage } from "@/hooks/useSiteImages";
import { PACKAGES, SLOT_LABELS, baseFor, computeTotal, currentSlot } from "@/lib/mockStore";
import { FeeTable } from "@/components/FeeTable";
import {
  EarlyBirdBanner,
  NewsTicker,
  Countdown,
  AbstractCallout,
  ExploreTinsukia,
} from "@/components/HomeExtras";

const HERITAGE_IMG_DEFAULT =
  "https://images.pexels.com/photos/10348767/pexels-photo-10348767.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1200";
const MEDICAL_IMG_DEFAULT =
  "https://images.pexels.com/photos/7088834/pexels-photo-7088834.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900";
const CONFERENCE_HALL_DEFAULT =
  "https://images.unsplash.com/photo-1462826303086-329426d1aef5?auto=format&fit=crop&w=1400&q=80";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const highlights = [
  {
    icon: Users,
    title: "300+ Delegates",
    body: "Internists, researchers and post-graduates from every corner of the country.",
  },
  {
    icon: BookOpen,
    title: "A 3-Day Academic Feast",
    body: "60+ enriching sessions bringing together contemporary medicine, practical clinical insights, expert perspectives and ideas that inspire better patient care.",
  },
  {
    icon: Award,
    title: "Global Faculty",
    body: "Renowned physicians and thought leaders shaping the future of internal medicine.",
  },
  {
    icon: Leaf,
    title: "Heritage Trail",
    body: "Optional evening walkthroughs of Tinsukia’s tea gardens and historic sites.",
  },
];


// Mock YouTube video IDs — swap with real APICON footage in a later phase.
const videos = [
  {
    id: "dQw4w9WgXcQ",
    tag: "Opening",
    title: "Lighting of the lamp — APICON 2025.",
    duration: "8:42",
  },
  {
    id: "M7lc1UVf-VE",
    tag: "Plenary",
    title: "Precision internal medicine — global panel.",
    duration: "42:15",
  },
  {
    id: "9bZkp7q19f0",
    tag: "Cultural",
    title: "Bihu evening — the heart of an Assam gathering.",
    duration: "5:20",
  },
  {
    id: "V-_O7nl0Ii0",
    tag: "Voices",
    title: "Why PGs fly across the country for APICON.",
    duration: "3:58",
  },
  {
    id: "e-ORhEE9VVg",
    tag: "Workshops",
    title: "POCUS hands-on — morning session recap.",
    duration: "6:14",
  },
  {
    id: "kJQP7kiw5Fk",
    tag: "Tinsukia",
    title: "A cinematic look at Assam's tea country.",
    duration: "4:05",
  },
];

const blogs = [
  {
    tag: "Programme",
    date: "Feb 12, 2026",
    title:
      "Announcing the plenary lineup: seven perspectives on precision internal medicine.",
    img: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=900&q=80",
  },
  {
    tag: "Registration",
    date: "Feb 08, 2026",
    title:
      "Early-bird registration now open — a step-by-step guide for API members and PGs.",
    img: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&w=900&q=80",
  },
  {
    tag: "Tinsukia",
    date: "Jan 30, 2026",
    title:
      "Travel notes: navigating Tinsukia, tea gardens, and the Dibru-Saikhowa reserve.",
    img: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&w=900&q=80",
  },
];

const Home = () => {
  const [videosList, setVideosList] = useState(videos);
  const [postsList, setPostsList] = useState([]);
  const [committeeList, setCommitteeList] = useState([]);
  const [galleryList, setGalleryList] = useState([]);
  const [settings, setSettings] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const heroLeft = useSiteImage("heroLeft", HERITAGE_IMG_DEFAULT);
  const heroRight = useSiteImage("heroRight", MEDICAL_IMG_DEFAULT);
  const whatImage = useSiteImage("whatImage", CONFERENCE_HALL_DEFAULT);
  useEffect(() => {
    videosApi.list().then((v) => { if (v?.length) setVideosList(v.map(x => ({ ...x, id: x.video_id }))); }).catch(() => {});
    postsApi.list().then((p) => {
      if (p?.length) setPostsList(p.map(x => ({ tag: x.tag, date: x.date, title: x.title, img: x.img })));
    }).catch(() => {});
    // Pull real committee members from CMS. Leadership tier comes first,
    // then whoever else — we display the top 4 as a home-page preview.
    committeeApi.list().then((list) => {
      if (Array.isArray(list) && list.length) {
        const rank = (m) => (m.group === "leadership" ? 0 : 1);
        setCommitteeList([...list].sort((a, b) => rank(a) - rank(b)).slice(0, 4));
      }
    }).catch(() => {});
    galleryApi.list().then((g) => {
      if (Array.isArray(g)) {
        setGalleryList(
          g.slice(0, 4).map((x, idx) => ({
            id: x.id || idx,
            src: x.src || x.url || "",
            title: x.title || "",
            caption: x.caption || x.title || "",
          })),
        );
      }
    }).catch(() => {});
    settingsApi
      .get()
      .then((s) => { if (s && Object.keys(s).length) setSettings(s); })
      .catch(() => {})
      .finally(() => setSettingsLoaded(true));
  }, []);
  const hero = {
    heroEyebrow: "35th Annual Conference · API",
    heroTitle: "",
    heroTagline: "APICON 2026 unites 300+ physicians, researchers and post-graduates in Tinsukia, Assam — for three days of rigorous science, warm conversation, and the unhurried beauty of the North-East.",
    primaryCtaLabel: "Register as a delegate",
    primaryCtaTo: "/register",
    secondaryCtaLabel: "Meet the committee",
    secondaryCtaTo: "/committee",
    siteTheme: "",
    whatEyebrow: "What to expect",
    whatTitle: "A conference shaped around the delegate's rhythm.",
    whatBody:
      "From dawn hospital rounds to twilight heritage walks, every session is designed to leave you sharper, calmer, and better connected to your peers.",
    whatImage: whatImage,
    whatImageCaptionTitle: "Auditorium · TMCH",
    whatImageCaptionBody: "Purpose-built halls with parallel workshop wings.",
    packagesEyebrow: "Registration Packages",
    packagesTitle: "Simple, transparent pricing.",
    packagesBody:
      "Early-bird rates for every delegate profile. All fees are inclusive of the 3-day conference kit, scientific sessions, workshops and cultural evenings.",
    videosEyebrow: "Video highlights",
    videosTitle: "A quick look at last December.",
    committeeEyebrow: "Organising committee",
    committeeTitle: "Led by physicians you already respect.",
    blogEyebrow: "From the journal",
    blogTitle: "Notes ahead of December.",
    // Section visibility toggles — default to true so existing behaviour is unchanged.
    showDetails: true,
    showHighlights: true,
    showVideos: true,
    showCommitteePreview: true,
    showBlogPreview: true,
    showFinalCta: true,
    // New: countdown / news / abstract / tourism defaults (overridden by CMS).
    countdownEnabled: true,
    countdownEventIso: "2026-12-18T09:00:00+05:30",
    countdownHeadline: "Hurry up — only {days} days left! Complete your registration at the earliest.",
    newsEnabled: true,
    newsItems: [],
    abstractEnabled: true,
    abstractEyebrow: "Call for abstracts is open",
    abstractTitle: "Submit your research — paper or e-poster",
    abstractTeaser: "",
    abstractChips: [],
    abstractPdfUrl: "",
    abstractPdfFilename: "APICON-2026-Abstract-Brochure.pdf",
    tourismEnabled: true,
    tourismEyebrow: "Before & after the seminar",
    tourismTitle: "Explore Tinsukia & nearby",
    tourismBody: "",
    tourismVideos: [],
    tourismPhotos: [],
    // Only overlay non-empty values so blank DB fields don't wipe defaults.
    ...Object.fromEntries(
      Object.entries(settings || {}).filter(([, v]) => v !== "" && v != null),
    ),
  };

  // Show a lightweight skeleton until CMS settings resolve — prevents the
  // hardcoded defaults from flashing in for a moment and then being replaced
  // by admin-edited copy (FOUC / ghost-data flicker).
  if (!settingsLoaded) {
    return (
      <div data-testid="page-home-skeleton" className="relative bg-white">
        <section className="relative overflow-hidden bg-white">
          <div className="pointer-events-none absolute inset-0 heritage-pattern opacity-60" />
          <div className="pointer-events-none absolute -top-32 -right-24 h-[520px] w-[520px] rounded-full bg-apicon-mist blur-3xl" />
          <div className="relative mx-auto grid w-full max-w-7xl gap-16 px-5 pb-24 pt-14 md:px-8 md:pb-28 md:pt-20 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-6 lg:pt-6">
              <div className="h-4 w-40 animate-pulse rounded-full bg-apicon-mist" />
              <div className="mt-6 h-14 w-full animate-pulse rounded-lg bg-apicon-mist" />
              <div className="mt-3 h-14 w-4/5 animate-pulse rounded-lg bg-apicon-mist" />
              <div className="mt-3 h-14 w-3/5 animate-pulse rounded-lg bg-apicon-mist" />
              <div className="mt-8 h-4 w-full animate-pulse rounded-full bg-apicon-mist/70" />
              <div className="mt-2 h-4 w-5/6 animate-pulse rounded-full bg-apicon-mist/70" />
              <div className="mt-2 h-4 w-2/3 animate-pulse rounded-full bg-apicon-mist/70" />
              <div className="mt-8 flex gap-3">
                <div className="h-12 w-48 animate-pulse rounded-full bg-apicon-navy/20" />
                <div className="h-12 w-48 animate-pulse rounded-full bg-apicon-mist" />
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-[3/4] w-full animate-pulse rounded-3xl bg-apicon-mist" />
                <div className="mt-10 aspect-[3/4] w-full animate-pulse rounded-3xl bg-apicon-mist" />
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="page-home">
      <EarlyBirdBanner />
      <NewsTicker enabled={hero.newsEnabled} items={hero.newsItems} />
      {/* ================= HERO ================= */}
      <section
        className="relative overflow-hidden bg-white"
        data-testid="hero-section"
      >
        <div className="pointer-events-none absolute inset-0 heritage-pattern opacity-60" />
        <div className="pointer-events-none absolute -top-32 -right-24 h-[520px] w-[520px] rounded-full bg-apicon-mist blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-[420px] w-[420px] rounded-full bg-apicon-gold/10 blur-3xl" />

        <div className="relative mx-auto grid w-full max-w-7xl gap-16 px-5 pb-24 pt-14 md:px-8 md:pb-28 md:pt-20 lg:grid-cols-12 lg:gap-10">
          {/* Left column: text */}
          <div className="lg:col-span-6 lg:pt-6">
            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={0}
            >
              <Badge
                className="rounded-full border-apicon-navy/15 bg-apicon-mist px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-apicon-navy hover:bg-apicon-mist"
                variant="outline"
                data-testid="hero-badge"
              >
                <Sparkles className="mr-2 h-3.5 w-3.5 text-apicon-gold" />
                {hero.heroEyebrow}
              </Badge>
            </motion.div>

            <motion.h1
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={1}
              className="mt-6 font-display text-[44px] font-bold leading-[1.02] tracking-tight text-apicon-navy sm:text-6xl lg:text-[64px]"
              data-testid="hero-title"
            >
              {hero.heroTitle ? (
                <span className="whitespace-pre-line">{hero.heroTitle}</span>
              ) : (
                <>
                  Advancing{" "}
                  <span className="relative inline-block">
                    <span className="relative z-10">Internal Medicine</span>
                    <span className="absolute inset-x-0 bottom-1 -z-0 h-3 rounded-sm bg-apicon-gold/60" />
                  </span>
                  <br className="hidden sm:block" />
                  <span className="italic text-apicon-green">Bridging science</span>{" "}
                  &amp; compassion.
                </>
              )}
            </motion.h1>

            {hero.siteTheme && (
              <motion.div
                initial="hidden"
                animate="show"
                variants={fadeUp}
                custom={1.5}
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-apicon-gold/30 bg-apicon-gold/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-apicon-navy"
                data-testid="hero-site-theme"
              >
                <Sparkles className="h-3.5 w-3.5 text-apicon-gold" />
                {hero.siteTheme}
              </motion.div>
            )}

            <motion.p
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={2}
              className="mt-6 max-w-xl text-base leading-relaxed text-gray-600 sm:text-lg"
              data-testid="hero-description"
            >
              {hero.heroTagline}
            </motion.p>

            {/* Meta strip */}
            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={3}
              className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm"
              data-testid="hero-meta"
            >
              <span className="inline-flex items-center gap-2 text-apicon-navy">
                <CalendarDays className="h-4 w-4 text-apicon-gold" />
                <span className="font-medium">18 – 20 December 2026</span>
              </span>
              <span className="hidden h-4 w-px bg-gray-300 sm:block" />
              <span className="inline-flex items-center gap-2 text-apicon-navy">
                <MapPin className="h-4 w-4 text-apicon-gold" />
                <span className="font-medium">
                  Tinsukia Medical College · Assam
                </span>
              </span>
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={4}
              className="mt-10 flex flex-wrap items-center gap-4"
              data-testid="hero-cta-group"
            >
              <Link to={hero.primaryCtaTo || "/register"} data-testid="hero-register-cta">
                <Button
                  size="lg"
                  className="group h-12 rounded-full bg-apicon-gold px-7 text-base font-semibold text-apicon-navy shadow-lg shadow-apicon-gold/40 hover:bg-[#ffb400] hover:text-apicon-navy"
                >
                  {hero.primaryCtaLabel}
                  <ArrowUpRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Button>
              </Link>
              <Link to={hero.secondaryCtaTo || "/committee"} data-testid="hero-committee-cta">
                <Button
                  variant="outline"
                  size="lg"
                  className="h-12 rounded-full border-apicon-navy/20 bg-white px-7 text-base font-medium text-apicon-navy hover:border-apicon-navy hover:bg-apicon-mist"
                >
                  {hero.secondaryCtaLabel}
                </Button>
              </Link>
            </motion.div>

            {/* Compact countdown — right under the CTAs */}
            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={4.5}
              className="mt-6"
            >
              <Countdown
                compact
                enabled={hero.countdownEnabled}
                eventIso={hero.countdownEventIso}
                headline={hero.countdownHeadline}
              />
            </motion.div>

            {/* Trust ticker */}
            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={5}
              className="mt-12 grid max-w-lg grid-cols-3 gap-6 border-t border-gray-200 pt-6"
              data-testid="hero-stats"
            >
              {[
                { k: "300+", v: "Delegates" },
                { k: "60+", v: "Sessions" },
                { k: "3", v: "Days" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-display text-3xl font-bold text-apicon-navy">
                    {s.k}
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-[0.16em] text-gray-500">
                    {s.v}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right column: dual-image mosaic */}
          <div className="relative lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative mx-auto aspect-[5/6] w-full max-w-[560px]"
              data-testid="hero-mosaic"
            >
              {/* Back / larger heritage image */}
              <div className="absolute inset-0 overflow-hidden rounded-[28px] shadow-2xl shadow-apicon-navy/15 ring-1 ring-black/5">
                <img
                  src={heroLeft}
                  alt="Tea gardens of Tinsukia, Assam"
                  className="h-full w-full object-cover"
                  loading="eager"
                  data-testid="hero-mosaic-heritage"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-apicon-navy/25 via-transparent to-transparent" />
                {/* Heritage caption chip */}
                <div className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-apicon-navy shadow-sm backdrop-blur">
                  <Leaf className="h-3.5 w-3.5 text-apicon-green" />
                  Assam · Heritage
                </div>
              </div>

              {/* Front / smaller medical image */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.9,
                  delay: 0.35,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="absolute -bottom-8 -left-6 w-[62%] rounded-3xl bg-white p-2 shadow-2xl shadow-apicon-navy/25 ring-1 ring-black/5 sm:-bottom-10 sm:-left-10"
                data-testid="hero-mosaic-medical-wrap"
              >
                <div className="overflow-hidden rounded-2xl">
                  <img
                    src={heroRight}
                    alt="Physician in consultation"
                    className="aspect-[4/5] h-full w-full object-cover"
                    loading="eager"
                    data-testid="hero-mosaic-medical"
                  />
                </div>
                {/* Floating stat pill */}
                <div className="absolute -right-4 -top-4 hidden items-center gap-2 rounded-2xl bg-white px-3 py-2 text-xs font-semibold text-apicon-navy shadow-lg ring-1 ring-black/5 sm:flex">
                  <HeartPulse className="h-4 w-4 text-apicon-gold" />
                  Internal Medicine · 2026
                </div>
              </motion.div>

              {/* Floating gold accent */}
              <div className="absolute -right-4 top-10 hidden h-24 w-24 animate-float-slow rounded-2xl bg-apicon-gold/90 shadow-xl shadow-apicon-gold/30 md:block">
                <div className="flex h-full w-full flex-col items-start justify-between p-3 text-apicon-navy">
                  <Stethoscope className="h-5 w-5" />
                  <div className="font-display text-xs font-bold leading-tight">
                    Dec
                    <br />
                    18–20
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <AbstractCallout
        enabled={hero.abstractEnabled}
        eyebrow={hero.abstractEyebrow}
        title={hero.abstractTitle}
        teaser={hero.abstractTeaser}
        chips={hero.abstractChips}
        pdfUrl={hero.abstractPdfUrl}
        pdfFilename={hero.abstractPdfFilename}
      />

      {/* ================= REGISTRATION PACKAGES ================= */}
      <section
        className="relative bg-white py-20 md:py-24"
        data-testid="packages-section"
      >
        <div className="mx-auto w-full max-w-7xl px-5 md:px-8">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div className="max-w-2xl">
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
                {hero.packagesEyebrow}
              </div>
              <h2 className="mt-3 whitespace-pre-line font-display text-4xl font-bold tracking-tight text-apicon-navy sm:text-5xl">
                {hero.packagesTitle}
              </h2>
              <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-gray-600 sm:text-lg">
                {hero.packagesBody}
              </p>
            </div>
            <div className="rounded-full border border-apicon-navy/10 bg-apicon-mist/60 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-apicon-navy">
              Prices include 18% GST
            </div>
          </div>

          <div
            className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
            data-testid="packages-grid"
          >
            {PACKAGES.map((pkg, i) => {
              const slot = currentSlot();
              const base = baseFor(pkg, slot);
              const total = computeTotal(base);
              const gst = total - base;
              const highlighted = pkg.id === "delegate-api";
              return (
                <motion.div
                  key={pkg.id}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={fadeUp}
                  custom={i}
                  className={`group relative flex flex-col overflow-hidden rounded-3xl border p-6 transition-all duration-300 ${
                    highlighted
                      ? "border-apicon-navy bg-apicon-navy text-white shadow-xl shadow-apicon-navy/15"
                      : "border-gray-100 bg-white hover:border-apicon-navy/30 hover:shadow-lg"
                  }`}
                  data-testid={`package-card-${pkg.id}`}
                >
                  {highlighted && (
                    <div className="absolute right-5 top-5 rounded-full bg-apicon-gold px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-apicon-navy">
                      Popular
                    </div>
                  )}
                  <div
                    className={`text-[10px] font-semibold uppercase tracking-[0.22em] ${
                      highlighted ? "text-apicon-gold" : "text-apicon-green"
                    }`}
                  >
                    Package
                  </div>
                  <div
                    className={`mt-2 font-display text-xl font-semibold leading-tight ${
                      highlighted ? "text-white" : "text-apicon-navy"
                    }`}
                  >
                    {pkg.label}
                  </div>
                  <p
                    className={`mt-2 text-sm leading-relaxed ${
                      highlighted ? "text-white/75" : "text-gray-600"
                    }`}
                  >
                    {pkg.description}
                  </p>

                  <div className="mt-5">
                    <div
                      className={`flex items-baseline gap-1 font-display font-bold tracking-tight ${
                        highlighted ? "text-white" : "text-apicon-navy"
                      }`}
                    >
                      <span className="text-4xl">₹{total.toLocaleString("en-IN")}</span>
                    </div>
                    <div
                      className={`mt-1 text-xs ${
                        highlighted ? "text-white/70" : "text-gray-500"
                      }`}
                    >
                      Base ₹{base.toLocaleString("en-IN")} + GST ₹
                      {gst.toLocaleString("en-IN")} · {SLOT_LABELS[slot]}
                    </div>
                  </div>

                  <ul
                    className={`mt-5 flex-1 space-y-2 text-sm ${
                      highlighted ? "text-white/85" : "text-gray-700"
                    }`}
                  >
                    <li className="flex items-start gap-2">
                      <Check
                        className={`mt-0.5 h-4 w-4 shrink-0 ${
                          highlighted ? "text-apicon-gold" : "text-apicon-green"
                        }`}
                      />
                      3-day conference access
                    </li>
                    <li className="flex items-start gap-2">
                      <Check
                        className={`mt-0.5 h-4 w-4 shrink-0 ${
                          highlighted ? "text-apicon-gold" : "text-apicon-green"
                        }`}
                      />
                      Delegate kit &amp; ID badge
                    </li>
                    <li className="flex items-start gap-2">
                      <Check
                        className={`mt-0.5 h-4 w-4 shrink-0 ${
                          highlighted ? "text-apicon-gold" : "text-apicon-green"
                        }`}
                      />
                      Workshops &amp; scientific sessions
                    </li>
                    <li className="flex items-start gap-2">
                      <Check
                        className={`mt-0.5 h-4 w-4 shrink-0 ${
                          highlighted ? "text-apicon-gold" : "text-apicon-green"
                        }`}
                      />
                      GST invoice on request
                    </li>
                  </ul>

                  <Link
                    to="/register"
                    className="mt-6"
                    data-testid={`package-register-${pkg.id}`}
                  >
                    <Button
                      className={`h-11 w-full rounded-full font-semibold ${
                        highlighted
                          ? "bg-apicon-gold text-apicon-navy hover:bg-[#ffb400]"
                          : "bg-apicon-navy text-white hover:bg-[#082C5A]"
                      }`}
                    >
                      Register Now
                    </Button>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <FeeTable />
        </div>
      </section>

      {/* ================= CONFERENCE DETAILS (BENTO) ================= */}
      {hero.showDetails && (
      <section
        className="relative bg-apicon-mist/60 py-20 md:py-24"
        data-testid="details-section"
      >
        <div className="mx-auto w-full max-w-7xl px-5 md:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
              The essentials
            </div>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-apicon-navy sm:text-5xl">
              A three-day gathering, thoughtfully hosted.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-gray-600 sm:text-lg">
              Anchored in the calm of upper Assam — designed around learning,
              conversation, and clinical excellence.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-6 md:gap-6">
            {/* Date card - large */}
            <div className="group relative overflow-hidden rounded-3xl bg-apicon-navy p-8 text-white md:col-span-3 md:row-span-2 md:p-10">
              <div className="absolute inset-0 opacity-[0.08] heritage-pattern" />
              <div className="relative">
                <CalendarDays className="h-8 w-8 text-apicon-gold" />
                <div className="mt-6 text-xs font-semibold uppercase tracking-[0.24em] text-apicon-gold/80">
                  When
                </div>
                <div className="mt-3 font-display text-5xl font-bold leading-none tracking-tight md:text-6xl">
                  18 – 20
                </div>
                <div className="mt-2 font-display text-2xl">
                  December, 2026
                </div>
                <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/75">
                  Three days of plenaries, symposia and workshops — Friday
                  through Sunday, with cultural evenings in between.
                </p>
              </div>
            </div>

            {/* Venue */}
            <div className="rounded-3xl bg-white p-8 shadow-sm ring-1 ring-black/5 md:col-span-3">
              <MapPin className="h-7 w-7 text-apicon-green" />
              <div className="mt-5 text-xs font-semibold uppercase tracking-[0.24em] text-apicon-green">
                Where
              </div>
              <div className="mt-2 font-display text-2xl font-semibold text-apicon-navy">
                Tinsukia Medical College &amp; Hospital
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Tinsukia, Assam · well-connected by air &amp; rail from
                Guwahati and Dibrugarh.
              </p>
            </div>

            {/* Theme */}
            <div className="rounded-3xl border border-apicon-navy/10 bg-white p-8 md:col-span-3">
              <Sparkles className="h-7 w-7 text-apicon-gold" />
              <div className="mt-5 text-xs font-semibold uppercase tracking-[0.24em] text-apicon-navy">
                Theme
              </div>
              <div className="mt-2 font-display text-2xl font-semibold italic text-apicon-navy">
                “{hero.siteTheme || "Advancing Internal Medicine: Bridging Science and Compassion."}”
              </div>
              <p className="mt-2 text-sm text-gray-600">
                A tribute to the physician-craft and the enduring warmth of the
                North-East.
              </p>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ================= ABOUT / HIGHLIGHTS ================= */}
      {hero.showHighlights && (
      <section
        className="relative bg-white py-20 md:py-28"
        data-testid="highlights-section"
      >
        <div className="mx-auto grid w-full max-w-7xl gap-14 px-5 md:px-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
              {hero.whatEyebrow}
            </div>
            <h2 className="mt-3 whitespace-pre-line font-display text-4xl font-bold tracking-tight text-apicon-navy sm:text-5xl">
              {hero.whatTitle}
            </h2>
            <p className="mt-5 max-w-md whitespace-pre-line text-base leading-relaxed text-gray-600">
              {hero.whatBody}
            </p>

            <div className="relative mt-10 overflow-hidden rounded-3xl ring-1 ring-black/5">
              <img
                src={hero.whatImage}
                alt="Conference hall interior"
                className="h-72 w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-apicon-navy/70 via-apicon-navy/10 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-apicon-gold">
                  {hero.whatImageCaptionTitle}
                </div>
                <div className="mt-1 font-display text-xl font-semibold">
                  {hero.whatImageCaptionBody}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:col-span-7">
            {highlights.map((h) => (
              <div
                key={h.title}
                className="group rounded-3xl border border-gray-100 bg-white p-7 transition-shadow duration-300 hover:shadow-xl hover:shadow-apicon-navy/5"
                data-testid={`highlight-${h.title.split(" ")[0].toLowerCase()}`}
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-apicon-mist text-apicon-navy transition-colors duration-300 group-hover:bg-apicon-gold group-hover:text-apicon-navy">
                  <h.icon className="h-5 w-5" />
                </span>
                <div className="mt-6 font-display text-xl font-semibold text-apicon-navy">
                  {h.title}
                </div>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">
                  {h.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      <ExploreTinsukia
        enabled={hero.tourismEnabled}
        eyebrow={hero.tourismEyebrow}
        title={hero.tourismTitle}
        body={hero.tourismBody}
        videos={hero.tourismVideos}
        photos={hero.tourismPhotos}
      />

      {/* ================= VIDEO HIGHLIGHTS (thin ribbon) ================= */}
      {hero.showVideos && (
      <section
        className="relative border-y border-gray-100 bg-white py-10 md:py-12"
        data-testid="video-highlights-section"
      >
        <div className="mx-auto w-full max-w-7xl px-5 md:px-8">
          <Carousel
            opts={{ align: "start", loop: true }}
            className="w-full"
            data-testid="videos-carousel"
          >
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-apicon-mist px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-apicon-navy">
                  <Youtube className="h-3.5 w-3.5 text-apicon-gold" />
                  {hero.videosEyebrow}
                </span>
                <span className="hidden text-sm text-gray-500 sm:inline">
                  {hero.videosTitle}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href="https://www.youtube.com"
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="videos-view-all"
                  className="hidden items-center gap-1 rounded-full border border-apicon-navy/20 bg-white px-4 py-1.5 text-xs font-medium text-apicon-navy transition-colors hover:border-apicon-navy hover:bg-apicon-mist md:inline-flex"
                >
                  Full playlist
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
                <div className="relative flex items-center gap-2">
                  <CarouselPrevious
                    className="static h-9 w-9 translate-x-0 translate-y-0 rounded-full border-apicon-navy/20 text-apicon-navy hover:bg-apicon-mist hover:text-apicon-navy"
                    data-testid="videos-prev"
                  />
                  <CarouselNext
                    className="static h-9 w-9 translate-x-0 translate-y-0 rounded-full border-apicon-navy/20 text-apicon-navy hover:bg-apicon-mist hover:text-apicon-navy"
                    data-testid="videos-next"
                  />
                </div>
              </div>
            </div>

            <CarouselContent className="-ml-4" data-testid="videos-track">
              {videosList.map((v, i) => (
                <CarouselItem
                  key={v.id || v.video_id}
                  className="pl-4 md:basis-1/2"
                  data-testid={`video-card-${i}`}
                >
                  <div className="group rounded-2xl bg-white p-2 ring-1 ring-black/5 transition-shadow duration-300 hover:shadow-lg hover:shadow-apicon-navy/10">
                    <div className="relative overflow-hidden rounded-xl bg-apicon-navy">
                      <div className="aspect-video w-full">
                        <iframe
                          src={`https://www.youtube.com/embed/${v.video_id || v.id}?rel=0`}
                          title={v.title}
                          loading="lazy"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          className="h-full w-full border-0"
                          data-testid={`video-iframe-${i}`}
                        />
                      </div>
                      <span className="pointer-events-none absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur">
                        <PlayCircle className="h-3 w-3 text-apicon-gold" />
                        {v.duration}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 px-2 pb-1 pt-3 text-xs">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-apicon-gold">
                        {v.tag}
                      </span>
                      <span className="h-1 w-1 rounded-full bg-gray-300" />
                      <span className="truncate font-medium text-apicon-navy">
                        {v.title}
                      </span>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      </section>
      )}

      {/* ================= COMMITTEE PREVIEW ================= */}
      {hero.showCommitteePreview && committeeList.length > 0 && (
      <section
        className="relative bg-apicon-mist/50 py-20 md:py-24"
        data-testid="committee-preview-section"
      >
        <div className="mx-auto w-full max-w-7xl px-5 md:px-8">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div className="max-w-xl">
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
                {hero.committeeEyebrow}
              </div>
              <h2 className="mt-3 whitespace-pre-line font-display text-4xl font-bold tracking-tight text-apicon-navy sm:text-5xl">
                {hero.committeeTitle}
              </h2>
            </div>
            <Link to="/committee" data-testid="committee-view-all">
              <Button
                variant="outline"
                className="rounded-full border-apicon-navy/20 text-apicon-navy hover:border-apicon-navy hover:bg-white"
              >
                View full committee
                <ArrowUpRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-2 gap-5 md:grid-cols-4 md:gap-6">
            {committeeList.map((c) => (
              <div
                key={c.id || c.name}
                className="group overflow-hidden rounded-3xl bg-white ring-1 ring-black/5 transition-transform duration-300 hover:-translate-y-1"
                data-testid={`committee-card-${(c.name || "member").split(" ").pop().toLowerCase()}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-apicon-mist">
                  {c.img ? (
                    <img
                      src={c.img}
                      alt={c.name}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-apicon-navy/30">
                      <Users className="h-16 w-16" />
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-apicon-navy/70 to-transparent" />
                </div>
                <div className="p-5">
                  <div className="font-display text-lg font-semibold text-apicon-navy">
                    {c.name}
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-[0.16em] text-apicon-green">
                    {c.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ================= BLOG PREVIEW ================= */}
      {hero.showBlogPreview && postsList.length > 0 && (
      <section
        className="relative bg-white py-20 md:py-24"
        data-testid="blog-preview-section"
      >
        <div className="mx-auto w-full max-w-7xl px-5 md:px-8">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div className="max-w-xl">
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
                {hero.blogEyebrow}
              </div>
              <h2 className="mt-3 whitespace-pre-line font-display text-4xl font-bold tracking-tight text-apicon-navy sm:text-5xl">
                {hero.blogTitle}
              </h2>
            </div>
            <Link to="/blog" data-testid="blog-view-all">
              <Button
                variant="ghost"
                className="rounded-full text-apicon-navy hover:bg-apicon-mist"
              >
                Read all posts
                <ArrowUpRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {postsList.map((b, i) => (
              <article
                key={b.title}
                className="group overflow-hidden rounded-3xl border border-gray-100 bg-white transition-shadow duration-300 hover:shadow-xl hover:shadow-apicon-navy/10"
                data-testid={`blog-card-${i}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={b.img}
                    alt={b.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.18em]">
                    <span className="text-apicon-gold">{b.tag}</span>
                    <span className="h-1 w-1 rounded-full bg-gray-300" />
                    <span className="text-gray-500">{b.date}</span>
                  </div>
                  <h3 className="mt-4 font-display text-xl font-semibold leading-snug text-apicon-navy">
                    {b.title}
                  </h3>
                  <div className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-apicon-navy transition-colors group-hover:text-apicon-green">
                    Continue reading
                    <ArrowUpRight className="h-4 w-4" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ================= GALLERY PREVIEW (above footer) ================= */}
      {galleryList.length > 0 && (
        <section
          className="relative bg-white py-20 md:py-24"
          data-testid="home-gallery-preview"
        >
          <div className="mx-auto w-full max-w-7xl px-5 md:px-8">
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
              <div className="max-w-xl">
                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
                  From past editions
                </div>
                <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-apicon-navy sm:text-5xl">
                  Moments &amp; memories
                </h2>
                <p className="mt-4 max-w-md text-base leading-relaxed text-gray-600">
                  A glimpse of the sessions, the cultural evenings, and the
                  friendships APICON has hosted through the years.
                </p>
              </div>
              <Link to="/gallery" data-testid="home-gallery-view-all">
                <Button
                  variant="outline"
                  className="rounded-full border-apicon-navy/20 text-apicon-navy hover:border-apicon-navy hover:bg-apicon-mist"
                >
                  View full gallery
                  <ArrowUpRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
              {galleryList.map((g, idx) => (
                <figure
                  key={g.id || idx}
                  className={cn(
                    "group relative overflow-hidden rounded-2xl bg-apicon-mist ring-1 ring-black/5",
                    // Alternate rows get a taller aspect for editorial rhythm.
                    idx % 3 === 0 ? "aspect-[4/5]" : "aspect-[4/3]",
                  )}
                  data-testid={`home-gallery-item-${idx}`}
                >
                  <img
                    src={g.src}
                    alt={g.title || g.caption || "APICON gallery"}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {g.caption && (
                    <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-apicon-navy/85 to-transparent p-3 text-[11px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 sm:text-xs">
                      {g.caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================= FINAL CTA ================= */}
      {hero.showFinalCta && (
      <section
        className="relative overflow-hidden bg-apicon-navy py-20 md:py-24"
        data-testid="final-cta-section"
      >
        <div className="pointer-events-none absolute inset-0 opacity-[0.08] heritage-pattern" />
        <div className="pointer-events-none absolute -bottom-20 -right-20 h-96 w-96 rounded-full bg-apicon-gold/20 blur-3xl" />

        <div className="relative mx-auto grid w-full max-w-7xl gap-10 px-5 md:px-8 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-8">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-gold">
              Registration is open
            </div>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl">
              Reserve your seat at <br className="hidden md:block" />
              APICON 2026.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
              Early-bird pricing for API members, non-API physicians and PGs —
              register now to lock in your delegate kit and workshop
              preferences.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 lg:col-span-4 lg:justify-end">
            <Link to="/register" data-testid="final-cta-register">
              <Button
                size="lg"
                className="h-12 rounded-full bg-apicon-gold px-7 text-base font-semibold text-apicon-navy shadow-lg shadow-apicon-gold/40 hover:bg-[#ffb400]"
              >
                Register now
                <ArrowUpRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/login" data-testid="final-cta-login">
              <Button
                size="lg"
                variant="outline"
                className="h-12 rounded-full border-white/30 bg-transparent px-7 text-base font-medium text-white hover:bg-white/10 hover:text-white"
              >
                I already have an account
              </Button>
            </Link>
          </div>
        </div>
      </section>
      )}
    </div>
  );
};

export default Home;
